import type { ReactNode } from "react";
import { Box, Chip, Divider, Grid, Stack, Typography } from "@mui/material";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import { Show, useRecordContext, type RaRecord, type ShowProps } from "react-admin";

import { EVENT_TYPE_CHOICES } from "@/constants";
import { formatListDate } from "@/utils/date-format";
import EventRsvpSection from "./EventRsvpSection";
import { EventShowToolbar } from "./EventShowToolbar";

const eventTypeLabels = Object.fromEntries(EVENT_TYPE_CHOICES.map(({ id, name }) => [id, name]));

const columnSx = {
  width: "100%",
  maxWidth: 1200,
  mr: "auto",
  ml: 0,
  textAlign: "left" as const,
};

function DetailItem({
  label,
  children,
  size = { xs: 12, sm: 6 },
}: {
  label: string;
  children: ReactNode;
  size?: { xs: number; sm?: number; md?: number };
}) {
  return (
    <Grid size={size}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5, fontWeight: 500 }}
      >
        {label}
      </Typography>
      {children}
    </Grid>
  );
}

function EventDetailsSection() {
  const record = useRecordContext<
    RaRecord & {
      name?: string;
      type?: string;
      hijriYear?: string | number;
      slug?: string;
      fromDate?: string;
      toDate?: string;
      rsvpEnabled?: boolean;
    }
  >();
  if (!record) {
    return null;
  }

  const typeLabel = eventTypeLabels[String(record.type ?? "")] ?? record.type ?? "—";

  return (
    <Box sx={columnSx}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 0.7, display: "block", mb: 1 }}
      >
        Event
      </Typography>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 1.5, lineHeight: 1.3 }}>
        {String(record.name ?? "—")}
      </Typography>

      <Grid container spacing={2}>
        <DetailItem label="Type">
          <Chip size="small" label={typeLabel} variant="outlined" />
        </DetailItem>
        <DetailItem label="Hijri year">
          <Typography variant="body2" fontWeight={500}>
            {record.hijriYear != null ? String(record.hijriYear) : "—"}
          </Typography>
        </DetailItem>
        <DetailItem label="Slug">
          <Typography variant="body2" fontWeight={500}>
            {record.slug?.trim() ? String(record.slug) : "—"}
          </Typography>
        </DetailItem>
        <DetailItem label="RSVP">
          <Chip
            size="small"
            label={record.rsvpEnabled === false ? "Disabled" : "Enabled"}
            color={record.rsvpEnabled === false ? "default" : "success"}
            variant="outlined"
          />
        </DetailItem>
      </Grid>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
        <CalendarMonthOutlined sx={{ fontSize: 18, color: "text.secondary" }} aria-hidden />
        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
          Dates
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <DetailItem label="From">
          <Typography variant="body2" fontWeight={500}>
            {formatListDate(record.fromDate, { empty: "—" })}
          </Typography>
        </DetailItem>
        <DetailItem label="To">
          <Typography variant="body2" fontWeight={500}>
            {formatListDate(record.toDate, { empty: "—" })}
          </Typography>
        </DetailItem>
      </Grid>
    </Box>
  );
}

const EventShowTitle = () => {
  const record = useRecordContext<{ name?: string }>();
  return <span>{record?.name ? String(record.name) : "Event"}</span>;
};

export const EventShow = (props: ShowProps) => (
  <Show actions={<EventShowToolbar />} title={<EventShowTitle />} {...props}>
    <Box
      component="main"
      sx={{
        width: "100%",
        maxWidth: 1200,
        ml: 0,
        mr: "auto",
        px: { xs: 1.25, sm: 2 },
        py: 1.25,
        pb: 2,
      }}
    >
      <Stack spacing={2} alignItems="flex-start">
        <EventDetailsSection />
        <Box sx={columnSx}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 0.7, display: "block", mb: 1 }}
          >
            RSVP statistics
          </Typography>
          <EventRsvpSection />
        </Box>
      </Stack>
    </Box>
  </Show>
);
